import { Close } from '@mui/icons-material';
import React from 'react';

import CAutocomplete from '../../../components/CAutocomplete';
import CButton from '../../../components/CButton';
import CLocalizedTextString from '../../../components/CLocalizedTextString';
import CPageModule from '../../../components/CPageModule';
import CPageModulePlot from '../../../components/CPageModulePlot';
import CStatefulContainer from '../../../components/CStatefulContainer';
import CTab from '../../../components/CTab';
import CTabItem from '../../../components/CTabItem';
import { BreakpointsInPx } from '../../../enums/BreakpointsInPx';
import { type EOptionsCalculatorParameters } from '../../../enums/EOptionsCalculatorParameters';
import { useAppSelector } from '../../../hooks/hooks';
import { type IAutocompleteOption } from '../../../models/IAutocompleteOption';
import { type I2DPlotConfig } from '../../../models/ICalculatorPlot';
import type { IOptionsCalculatorConfig } from '../../../models/IOptionsCalculatorConfig';
import type { IResponse } from '../../../models/IResponse';
import type { IResponseTableOrPlot } from '../../../models/IResponseTableOrPlot';
import type { PlotlyResponse } from '../../../models/PlotlyModel';

interface Props {
  optionsTableDerivatives: IResponse<{ options: Array<{ label: string; value: string }> }>;
  plot2d: IResponseTableOrPlot<{ call: PlotlyResponse; put: PlotlyResponse }>;
  parameterOptions: IAutocompleteOption[];
  plotConfig: I2DPlotConfig;
  showDeleteButton?: boolean;
  onUpdatePlotConfig: (configUpdate: Partial<I2DPlotConfig>) => void;
  onDeletePlot: () => void;
  textPrefix: string;
  plotIndex: number;
  textSuffix: string;
  optionsConfigDefault: IOptionsCalculatorConfig;
}

export default function CCalculator2dPlot(props: Props) {
  const { plotConfig, onUpdatePlotConfig, onDeletePlot } = props;
  const { dashboardOutletDimensions } = useAppSelector((state) => state.dashboard);

  const cLocalizedTextStringDerivatives = CLocalizedTextString('topics.calculator.page.derivatives');
  const cLocalizedTextStringParameters = CLocalizedTextString('topics.calculator.page.parameters');
  const cLocalizedTextStringPut = CLocalizedTextString('topics.calculator.page.put');
  const cLocalizedTextStringCall = CLocalizedTextString('topics.calculator.page.call');

  const handlePlotXChange = (options: IAutocompleteOption[]) => {
    const newPlotX = options[0]?.key as EOptionsCalculatorParameters;
    if (newPlotX) {
      onUpdatePlotConfig({ plotX: newPlotX });
    }
  };

  const handlePlotYChange = (options: IAutocompleteOption[]) => {
    const newPlotY =
      options.length > 0
        ? options
            .slice(options.length > 2 ? 1 : 0, options.length > 2 ? 3 : 2) // Slice to limit the item counts to 2, FIFO ordering
            .map((e) => e.key)
        : [props.optionsConfigDefault.plot2dConfigs?.[0].plotY?.[0] ?? ''];
    onUpdatePlotConfig({ plotY: newPlotY });
  };

  const deleteButton = (
    <CButton
      size={'textfield'}
      icons={{ start: <Close /> }}
      onClick={onDeletePlot}
      isForm={true}
      style={{ borderWidth: 1 }}
    />
  );

  return (
    <>
      <CPageModule submodule>
        <p>
          {props.textPrefix}
          {props.textSuffix && props.plotIndex + 1}
          {props.textSuffix}
        </p>
        <div className={'flex gap-2'}>
          <div
            className={'grid gap-2 items-stretch flex-grow'}
            style={{
              gridTemplateColumns:
                dashboardOutletDimensions.w <= BreakpointsInPx.BREAKPOINT_3
                  ? 'repeat(1, minmax(0, 1fr))'
                  : 'repeat(2, minmax(0, 1fr))'
            }}
          >
            <span>
              <CAutocomplete
                options={props.parameterOptions}
                value={[
                  {
                    label: plotConfig.plotX,
                    key: plotConfig.plotX
                  }
                ]}
                textFieldLabel={cLocalizedTextStringParameters}
                onChange={handlePlotXChange}
              />
            </span>
            <span className={'flex gap-2'}>
              <span className={'flex-grow'}>
                <CAutocomplete
                  textFieldLabel={cLocalizedTextStringDerivatives}
                  multiple
                  value={plotConfig.plotY.map((e) => ({ label: e, key: e }))}
                  onChange={handlePlotYChange}
                  options={
                    props.optionsTableDerivatives.data?.options.map((e) => ({ label: e.label, key: e.value })) ?? []
                  }
                />
              </span>
              {props.showDeleteButton && dashboardOutletDimensions.w <= BreakpointsInPx.BREAKPOINT_3 && deleteButton}
            </span>
          </div>
          {props.showDeleteButton && dashboardOutletDimensions.w > BreakpointsInPx.BREAKPOINT_3 && deleteButton}
        </div>
      </CPageModule>
      <CStatefulContainer submodule apiRequestState={props.plot2d.state}>
        <CTab submodule>
          <CTabItem label="Call" text={cLocalizedTextStringCall}>
            <CPageModulePlot data={props.plot2d.data?.call?.data} layout={props.plot2d.data?.call?.layout} />
          </CTabItem>
          <CTabItem label="Put" text={cLocalizedTextStringPut}>
            <CPageModulePlot data={props.plot2d.data?.put?.data} layout={props.plot2d.data?.put?.layout} />
          </CTabItem>
        </CTab>
      </CStatefulContainer>
    </>
  );
}
